import clsx from 'clsx'
import { useEffect, useMemo } from 'react'

import { FvButton, type IconName } from '@fv/client-components'

import { EmptyList } from '@/components/shared/EmptyList'
import type { Opportunity } from '@/types/Opportunity'

import { MessagingPanel } from '../messaging/MessagingPanel'
import { Details } from '../upload/Details'
import { Documents } from '../upload/Documents'
import type { OppPanelTab } from './types'

type Props = {
  currentTab?: OppPanelTab
  onTabChange?: (tab: OppPanelTab) => void
  opportunity?: Opportunity
  isOpen?: boolean
}

const tabs: Array<{
  icon: IconName
  label: string
  value: OppPanelTab
}> = [
  { value: 'messages', label: 'Messages', icon: 'comment-alt-dots' },
  { value: 'documents', label: 'Documents', icon: 'file' },
  { value: 'details', label: 'Details', icon: 'list' },
]

export const OppPanel = ({
  currentTab,
  opportunity,
  onTabChange,
  isOpen = false,
}: Props) => {
  const showDetailsTab =
    !!opportunity &&
    (opportunity.status === 'awarded' ||
      opportunity.status === 'confirmed' ||
      opportunity.isArchived)
  const availableTabs = useMemo(
    () => tabs.filter(tab => tab.value !== 'details' || showDetailsTab),
    [showDetailsTab],
  )

  useEffect(() => {
    if (!availableTabs.some(tab => tab.value === currentTab)) {
      onTabChange?.('messages')
    }
  }, [currentTab, availableTabs, onTabChange])

  return (
    <aside
      className={clsx(
        'col-start-3 row-start-2 row-span-2 b500:w-full b500:border-0 border-l border-fv-gray',
        {
          flyout: isOpen,
        },
      )}
    >
      {opportunity && (
        <nav className="bg-fv-blue-50 border-b border-b-fv-blue-200 fa-width-auto flex mb-6 px-6 py-4 [&>*]:px-2 [&>*:first-child]:pl-0">
          {availableTabs.map(tab => {
            const isActive = currentTab === tab.value

            return (
              <div key={tab.value}>
                <FvButton
                  className={clsx(
                    isActive &&
                      'bg-fv-blue-100 rounded-full px-3 py-2 border-2 border-fv-blue-200',
                  )}
                  icon={tab.icon}
                  iconClass={clsx(isActive && 'text-fv-orange-bright')}
                  onClick={() => onTabChange?.(tab.value)}
                >
                  {tab.label}
                </FvButton>
              </div>
            )
          })}
        </nav>
      )}
      <div
        className={clsx(
          'flex-1 overflow-auto px-6 flex flex-col items-stretch pb-8',
          { 'pt-6': !opportunity },
        )}
      >
        {!opportunity ? (
          <EmptyList message="Select a load to view messages, documents, and details." />
        ) : null}

        {opportunity && currentTab === 'messages' ? (
          <MessagingPanel opportunity={opportunity} />
        ) : null}

        {opportunity && currentTab === 'documents' ? (
          <Documents opportunity={opportunity} />
        ) : null}

        {opportunity && currentTab === 'details' ? (
          <Details opportunity={opportunity} />
        ) : null}
      </div>
    </aside>
  )
}
